import { dcos, dsin, fbm2, hash32 } from '@vm/shared';
import { family, M } from '../palette';
import { blocks, bricks, MapBuilder, planks, smoothField, solid, STAIR_FOOT, STAIR_LAND, STAIR_RISE, STAIR_TREAD, yawToward, type Brush, type Side } from './builder';
import { emptyMeta, type MapDef, type MapMeta, type PickupSpot } from './types';

const S = 1280;
const HEIGHT = 224;
/**
 * First air above the sea. The sea is shallow everywhere (a sand shelf by the coast, a dark
 * "deep water" floor further out) so looking through the water never shows past the map edge.
 * Everything below y = BED is bedrock.
 */
const SEA = 72;
const BED = 32;
/** Highest y a tree may reach. */
const TREE_CAP = 127;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

interface Poi {
  name: string;
  x: number;
  z: number;
  r: number;
  /** Ground level (first air) of the flattened pad. */
  y: number;
}

const POIS: Record<string, Poi> = {
  town: { name: 'Town', x: 640, z: 640, r: 190, y: SEA + 12 },
  farm: { name: 'Farm', x: 350, z: 360, r: 160, y: SEA + 9 },
  lighthouse: { name: 'Lighthouse', x: 1030, z: 250, r: 95, y: SEA + 20 },
  radio: { name: 'Radio Hill', x: 330, z: 960, r: 95, y: SEA + 45 },
  docks: { name: 'Docks', x: 930, z: 990, r: 80, y: SEA + 3 },
  ruins: { name: 'Ruins', x: 175, z: 640, r: 100, y: SEA + 10 },
  camp: { name: 'Camp', x: 690, z: 175, r: 90, y: SEA + 10 },
  quarry: { name: 'Quarry', x: 1085, z: 640, r: 95, y: SEA + 16 },
};

/** Paths from the town to every other POI (dirt, lightly flattened). */
const PATHS: [number, number, number, number][] = Object.values(POIS)
  .filter((p) => p.name !== 'Town')
  .map((p) => [POIS.town.x, POIS.town.z, p.x, p.z]);

const segDist = (px: number, pz: number, ax: number, az: number, bx: number, bz: number): number => {
  const vx = bx - ax;
  const vz = bz - az;
  const t = clamp01(((px - ax) * vx + (pz - az) * vz) / (vx * vx + vz * vz));
  return Math.hypot(px - ax - vx * t, pz - az - vz * t);
};

/** Island terrain: squircle coastline with noisy bays, hills, flattened POI pads and paths. */
function terrain(b: MapBuilder): { h: Int16Array; surf: Uint8Array } {
  const h = new Int16Array(S * S);
  const land = new Float32Array(S * S);
  const coast = new Float32Array(S * S);
  const surf = new Uint8Array(S * S);
  const sub = new Uint8Array(S * S);
  const path = new Uint8Array(S * S);
  const coastN = smoothField(S, S, 8, (x, z) => fbm2(x * 0.0035, z * 0.0035, 501, 3));
  const hillN = smoothField(S, S, 8, (x, z) => fbm2(x * 0.0042, z * 0.0042, 502, 1));
  const tint = smoothField(S, S, 8, (x, z) => fbm2(x * 0.008, z * 0.008, 503, 2));
  // Pass 1: coastline and raw heights (land keeps a float height for the blends below).
  for (let z = 0; z < S; z++)
    for (let x = 0; x < S; x++) {
      const u = (x - 640) / 575;
      const v = (z - 640) / 575;
      const u2 = u * u;
      const v2 = v * v;
      let q = Math.sqrt(Math.sqrt(u2 * u2 + v2 * v2)) + (coastN(x, z) - 0.5) * 0.28;
      // The docks bay bites into the south-east coast.
      const db = Math.hypot(x - 1060, z - 1090);
      if (db < 190) q += (1 - db / 190) * 0.32;
      const i = x + z * S;
      if (q < 1) {
        const e = clamp01((1 - q) / 0.14);
        coast[i] = e;
        const hill = Math.max(0, hillN(x, z) - 0.52) * 115 * e;
        const dr = Math.hypot(x - 330, z - 960);
        const dc = Math.hypot(x - 1030, z - 250);
        const bumps = (dr < 260 ? (1 - dr / 260) * 40 : 0) + (dc < 220 ? (1 - dc / 220) * 22 : 0);
        land[i] = SEA + 1 + e * 9 + hill + bumps * e;
      } else {
        land[i] = -1;
        // Sand shelf under clear water by the coast; beyond it the "deep sea" is an opaque
        // blue surface flush with the water line (one draw call per region, no see-through).
        h[i] = (q - 1) * 600 < 16 ? SEA - 2 : SEA;
      }
    }
  // Pass 2: flatten POI pads and ease the paths (only inside their bounding boxes).
  for (const p of Object.values(POIS)) {
    const R = p.r + 50;
    for (let z = Math.max(0, p.z - R); z < Math.min(S, p.z + R); z++)
      for (let x = Math.max(0, p.x - R); x < Math.min(S, p.x + R); x++) {
        const i = x + z * S;
        if (land[i] < 0) continue;
        const wgt = clamp01((R - Math.hypot(x - p.x, z - p.z)) / 50);
        if (wgt > 0) land[i] += (p.y - land[i]) * wgt;
      }
  }
  for (const [ax, az, bx, bz] of PATHS) {
    for (let z = Math.max(0, Math.min(az, bz) - 26); z < Math.min(S, Math.max(az, bz) + 26); z++)
      for (let x = Math.max(0, Math.min(ax, bx) - 26); x < Math.min(S, Math.max(ax, bx) + 26); x++) {
        const i = x + z * S;
        if (land[i] < 0) continue;
        const d = segDist(x, z, ax, az, bx, bz);
        if (d >= 26) continue;
        if (d < 9) path[i] = 1;
        if (land[i] > SEA + 8) land[i] -= (land[i] - (SEA + 8)) * 0.15 * clamp01((26 - d) / 18);
      }
  }
  // Pass 3: terrace and pick materials. Terrace heights are taken per 8 x 8 block so contour
  // lines step in 0.5 m increments: far fewer, larger faces than a 1-voxel staircase.
  const grass = [M('grass_2'), M('grass_2'), M('grass_1'), M('grass_1')];
  const sand1 = M('sand_1');
  const sand2 = M('sand_2');
  const sand3 = M('sand_3');
  const dirt = M('dirt_1');
  const trail = M('dirt_3');
  const rock = M('rock_2');
  const grassSub = M('grass_4');
  const deepSea = M('deep_water');
  for (let z = 0; z < S; z++)
    for (let x = 0; x < S; x++) {
      const i = x + z * S;
      if (land[i] < 0) {
        // Clear water over the sand shelf; further out the opaque sea is liquid a few voxels down,
        // so players wade into it instead of walking on top.
        const shelf = h[i] === SEA - 2;
        surf[i] = shelf ? sand1 : deepSea;
        sub[i] = shelf ? sand2 : deepSea;
        continue;
      }
      // Terrace blocks 0.8 x 0.8 m (one merged top quad each) rising in 0.5 m steps (within the auto-step, so slopes stay walkable).
      const bi = (x & ~7) + 4 + ((z & ~7) + 4) * S;
      const lb = land[bi] >= 0 ? land[bi] : land[i];
      const y = SEA + Math.max(1, Math.floor((lb - SEA - 1) / 5) * 5 + 1);
      h[i] = y;
      const t = tint(x, z);
      if (y <= SEA + 3) {
        surf[i] = sand1;
        sub[i] = sand2;
      } else if (y <= SEA + 6 && coast[i] < 0.55) {
        surf[i] = sand3;
        sub[i] = sand2;
      } else if (path[i]) {
        surf[i] = trail;
        sub[i] = dirt;
      } else if (y >= SEA + 62) {
        surf[i] = rock;
        sub[i] = rock;
      } else {
        // Two shades in broad meadows (large patches keep the top faces merging into big quads).
        surf[i] = t > 0.56 ? grass[2] : grass[0];
        sub[i] = grassSub;
      }
    }
  b.heightfield({
    height: h, surface: surf, sub: sand2, subCol: sub, deep: sand2, subDepth: 4, bedrockTop: BED, bedrockMin: BED + 2,
    water: M('water'), waterLevel: SEA,
  });
  return { h, surf };
}

/** Collects loot spots with a repeating mix of kinds. */
class Loot {
  readonly spots: PickupSpot[] = [];
  private static readonly MIX: PickupSpot['kind'][] = ['weapon', 'ammo', 'weapon', 'health', 'armor', 'ammo', 'weapon', 'gadget', 'health', 'ammo', 'armor', 'weapon'];
  constructor(private b: MapBuilder) {}
  /**
   * Adds a spot at (x, z) in voxels, standing on the first surface below `fromY`; if something
   * is in the way it moves outward (up to ~2.5 m) to the nearest clear, level spot.
   */
  add(x: number, z: number, fromY: number, kind?: PickupSpot['kind']): void {
    const k = kind ?? Loot.MIX[this.spots.length % Loot.MIX.length];
    for (let r = 0; r <= 24; r += 3)
      for (let a = 0; a < Math.max(1, r * 2); a++) {
        const ang = (a / Math.max(1, r * 2)) * Math.PI * 2;
        const px = x + Math.round(dcos(ang) * r);
        const pz = z + Math.round(dsin(ang) * r);
        const y = this.clearSpot(px, pz, fromY);
        if (y < 0) continue;
        this.spots.push({ kind: k, x: (px + 0.5) / 10, y: y / 10, z: (pz + 0.5) / 10 });
        return;
      }
    this.spots.push({ kind: k, x: (x + 0.5) / 10, y: this.b.surfaceY(x, z, fromY) / 10, z: (z + 0.5) / 10 });
  }

  /** Surface y if the 5 x 5 footprint around (x, z) is level with 8 voxels of air above, else -1. */
  private clearSpot(x: number, z: number, fromY: number): number {
    const y = this.b.surfaceY(x, z, fromY);
    const w = this.b.w;
    for (let dz = -2; dz <= 2; dz++)
      for (let dx = -2; dx <= 2; dx++) {
        if (w.get(x + dx, y - 1, z + dz) === 0) return -1;
        for (let dy = 0; dy < 8; dy++) if (w.get(x + dx, y + dy, z + dz) !== 0) return -1;
      }
    return y;
  }
}

/** Climb of each lighthouse flight, voxels; the stairs block is STAIR_FOOT + the run of one flight + STAIR_LAND long. */
const LIGHT_RISE = 16;
const lightRun = (): number => Math.ceil(LIGHT_RISE / STAIR_RISE) * STAIR_TREAD;

/**
 * Switchback stairs in a shaft: 34 degree flights of `rise` voxels each, climbing +x in the first lane (z0 to z0 + lane) and -x in the second, with a landing
 * across both lanes at each end. The first flight has the 2.0 m foot before it (the shaft's west end, x0); every later flight starts on the landing of the one
 * before. Occupies STAIR_FOOT + run + STAIR_LAND voxels along x from x0 and 2 * lane across. Returns the x where the last (east-going) flight ends.
 */
function switchback(b: MapBuilder, x0: number, z0: number, lane: number, rise: number, y0: number, y1: number, br: Brush): number {
  const west = x0 + STAIR_FOOT;
  let y = y0;
  let k = 0;
  let end = west;
  while (y < y1) {
    const h = Math.min(rise, y1 - y);
    const run = Math.ceil(h / STAIR_RISE) * STAIR_TREAD;
    const east = k % 2 === 0;
    const f = b.flight(east ? west : west + run - 1, y, z0 + (east ? 0 : lane), east ? 'e' : 'w', lane, h, br, STAIR_RISE, STAIR_TREAD, 3);
    if (k > 0) f.landing = true; // starts on the landing, no foot of its own
    y += h;
    const lx = east ? west + run : west - STAIR_LAND;
    b.box(lx, y - 3, z0, lx + STAIR_LAND, y, z0 + lane * 2, br);
    end = west + run;
    k++;
  }
  return end;
}

function town(b: MapBuilder, loot: Loot): void {
  const p = POIS.town;
  const g = p.y;
  const cob = blocks([M('cobble_1'), M('cobble_2')], 3, 11);
  b.box(p.x - 90, g - 1, p.z - 90, p.x + 90, g, p.z + 90, cob);
  b.fountain(p.x, g, p.z, 22);
  const houses: { x0: number; z0: number; x1: number; z1: number; floors: number; wall: Brush; roof: Brush; style: 'gable' | 'gableX' | 'flat'; door: Side }[] = [
    { x0: 540, z0: 450, x1: 622, z1: 536, floors: 2, wall: bricks(family('brick_red'), 1), roof: M('roof_red_1'), style: 'gable', door: 's' },
    { x0: 662, z0: 450, x1: 744, z1: 536, floors: 2, wall: solid([M('plaster_1')]), roof: M('roof_blue'), style: 'gable', door: 's' },
    { x0: 540, z0: 744, x1: 622, z1: 820, floors: 1, wall: bricks([M('brick_yellow')], 0), roof: M('roof_red_2'), style: 'gable', door: 'n' },
    { x0: 662, z0: 744, x1: 760, z1: 830, floors: 2, wall: bricks([M('brick_gray')], 0), roof: M('concrete_4'), style: 'flat', door: 'n' },
    { x0: 446, z0: 580, x1: 530, z1: 690, floors: 2, wall: solid([M('plaster_2')]), roof: M('roof_slate'), style: 'gableX', door: 'e' },
    { x0: 752, z0: 590, x1: 836, z1: 700, floors: 1, wall: bricks([M('brick_orange')], 0), roof: M('roof_red_1'), style: 'gableX', door: 'w' },
  ];
  for (const hs of houses) {
    b.building({
      x0: hs.x0, z0: hs.z0, x1: hs.x1, z1: hs.z1, y0: g - 1, floors: hs.floors, wall: hs.wall, roof: hs.roof, roofStyle: hs.style, trim: M('stone_2'),
      windows: { w: 8, h: 9, sill: 9, spacing: 18, glass: null }, doors: [{ side: hs.door, w: 12 }],
    });
    const cx = (hs.x0 + hs.x1) >> 1;
    const cz = (hs.z0 + hs.z1) >> 1;
    loot.add(cx, cz + 12, g + 10);
    if (hs.floors > 1) loot.add(cx + 14, cz, g + 40);
  }
  // Plaza furniture.
  for (const [x, z] of [[570, 570], [710, 570], [570, 710], [710, 710]]) b.lamp(x, g, z, 34);
  b.bench(590, g, 600);
  b.bench(676, g, 676);
  b.stall(560, g, 640, M('cloth_red'));
  b.car(690, g, 610, M('paint_blue'), false);
  b.leafyTree(600, g, 690, 36, 12, M('leaves_2'));
  loot.add(640, 600, g + 10);
  loot.add(600, 660, g + 10);
  loot.add(724, 690, g + 10);
}

function farm(b: MapBuilder, loot: Loot): void {
  const p = POIS.farm;
  const g = p.y;
  // Big red barn with a hay loft, a silo and wheat fields.
  b.building({
    x0: 270, z0: 280, x1: 380, z1: 360, y0: g - 1, floors: 1, floorH: 46, wall: planks([M('paint_red')], 0), roof: M('roof_slate'),
    roofStyle: 'gableX', trim: M('stone_4'), windows: { w: 8, h: 8, sill: 26, spacing: 24, glass: null }, doors: [{ side: 's', w: 30, h: 34 }, { side: 'n', w: 14 }],
  });
  b.box(274, g + 22, 284, 330, g + 24, 356, planks([M('planks_2')], 0));
  b.climb(330, g, 330, 'n', 12, 24, planks([M('planks_2')], 0), 3); // runs north along the loft's east edge, 34 degrees: the first step is 2.7 m from the south wall
  for (const [x, z] of [[280, 290], [292, 290], [286, 300]]) b.haybale(x, g + 24, z);
  b.cylinder(420, 320, g, g + 80, 16, M('metal_3'));
  b.cylinder(420, 320, g + 1, g + 79, 13, 0);
  b.dome(420, g + 80, 320, 16, M('roof_red_2'));
  b.clear(404, g, 314, 410, g + 22, 326);
  for (let x = 250; x < 470; x += 6) b.box(x, g, 400, x + 4, g + 6, 480, M('wheat'));
  b.fence(246, 396, 474, 396, g);
  b.fence(246, 484, 474, 484, g);
  b.car(300, g, 380, M('paint_green'), true);
  for (const [x, z] of [[400, 380], [412, 380], [406, 386]]) b.haybale(x, g, z, false);
  loot.add(300, 330, g + 10);
  loot.add(300, 300, g + 30);
  loot.add(350, 330, g + 10);
  loot.add(420, 320, g + 10);
  loot.add(460, 390, g + 10);
}

function lighthouse(b: MapBuilder, loot: Loot): void {
  const p = POIS.lighthouse;
  const g = p.y;
  const cx = p.x;
  const cz = p.z;
  const top = g + 80;
  // A 34 degree switchback needs a 2.0 m foot, a 2.4 m run and a 1.0 m landing along one lane pair: 5.4 m, so the shaft is 5.7 m across (it was 4.1 m).
  const R = 32;
  const IN = 28;
  const white = M('paint_white');
  const red = M('paint_red');
  const steel = M('steel_dark_1');
  // Striped octagonal tower around a shaft, with a gallery ring at the top.
  for (let y = g; y < top; y += 12) b.octagon(cx, cz, y, Math.min(top, y + 12), R, ((y - g) / 12) % 2 === 0 ? white : red);
  b.octagon(cx, cz, top - 2, top, R + 7, steel);
  b.octagon(cx, cz, top, top + 8, R + 7, steel);
  b.octagon(cx, cz, top, top + 8, R + 6, 0);
  b.octagon(cx, cz, g, top, IN, 0);
  b.box(cx - IN, g - 1, cz - IN, cx + IN + 1, g, cz + IN + 1, M('stone_4'));
  b.clear(cx - R - 1, g, cz - 6, cx - IN + 1, g + 22, cz + 6);
  // Lookout floor over the east part of the shaft, flush with the top of the last flight; the
  // west part stays open as the stairwell, with a rail beside the arrival lane.
  b.octagon(cx, cz, top - 2, top, IN, steel);
  const endX = cx - IN + STAIR_FOOT + lightRun(); // where the last flight arrives and the lookout floor starts
  b.clear(cx - IN, top - 2, cz - IN, endX, top, cz + IN + 1);
  b.box(endX, top, cz - IN, endX + 1, top + 10, cz - 10, M('paint_red'));
  b.box(endX, top, cz, endX + 1, top + 10, cz + IN + 1, M('paint_red'));
  // Switchback stairs (two 1.0 m lanes, 1.0 m landings, 34 degrees) climb the shaft to the lookout floor; the lanes stay inside the shaft's straight arm.
  switchback(b, cx - IN, cz - 10, 10, LIGHT_RISE, g, top, M('planks_dark_1'));
  // Lantern on a deck held up by four posts on the tower wall (walk under it on the lookout).
  for (const [dx, dz] of [[R - 3, -1], [-R + 1, -1], [-1, R - 3], [-1, -R + 1]]) b.box(cx + dx, top, cz + dz, cx + dx + 2, top + 22, cz + dz + 2, steel);
  b.octagon(cx, cz, top + 22, top + 24, R, steel);
  b.octagon(cx, cz, top + 24, top + 40, 11, M('glass'));
  b.octagon(cx, cz, top + 25, top + 39, 10, 0);
  b.box(cx - 4, top + 28, cz - 4, cx + 5, top + 36, cz + 5, M('neon_yellow'));
  b.cone(cx, top + 40, cz, 14, 12, red);
  // Keeper's cottage.
  b.building({
    x0: 950, z0: 290, x1: 1010, z1: 340, y0: g - 1, floors: 1, wall: solid([M('paint_white')]), roof: M('roof_blue'), trim: M('stone_2'),
    windows: { w: 7, h: 8, sill: 9, spacing: 16, glass: null }, doors: [{ side: 'e', w: 12 }],
  });
  loot.add(cx + 4, cz + 13, g + 10);
  loot.add(cx + 20, cz + 6, top + 4, 'weapon');
  loot.add(980, 315, g + 10);
}

function radio(b: MapBuilder, loot: Loot): void {
  const p = POIS.radio;
  const g = p.y;
  b.box(p.x - 30, g - 1, p.z - 30, p.x + 30, g, p.z + 30, blocks([M('concrete_2')], 5, 1));
  b.lattice(p.x - 10, g, p.z - 10, p.x + 10, g + 80, p.z + 10, 'y', M('paint_red'), 2, 14);
  b.box(p.x - 2, g + 80, p.z - 2, p.x + 2, g + 94, p.z + 2, M('steel_dark_1'));
  b.box(p.x - 1, g + 94, p.z - 1, p.x + 2, g + 96, p.z + 2, M('neon_red'));
  for (const [dx, dz] of [[-60, -60], [60, -60], [-60, 60], [60, 60]]) {
    const ax = p.x + dx;
    const az = p.z + dz;
    const ay = b.surfaceY(ax, az, HEIGHT - 1);
    b.box(ax - 2, ay - 1, az - 2, ax + 3, ay + 2, az + 3, M('concrete_4'));
    b.line(ax, ay + 1, az, p.x + Math.sign(dx) * 9, g + 60, p.z + Math.sign(dz) * 9, M('steel_dark_2'));
  }
  // Equipment bunker and a dish.
  b.building({
    x0: p.x + 20, z0: p.z - 20, x1: p.x + 66, z1: p.z + 22, y0: g - 1, floors: 1, floorH: 26, wall: blocks([M('concrete_1')], 5, 2), trim: M('concrete_4'),
    roofStyle: 'flat', parapet: 2, windows: { w: 10, h: 5, sill: 12, spacing: 20, glass: null }, doors: [{ side: 'w', w: 12 }],
  });
  for (let dy = -10; dy <= 10; dy++)
    for (let dz = -10; dz <= 10; dz++) {
      const r2 = dy * dy + dz * dz;
      if (r2 > 100) continue;
      b.set(p.x - 36 + Math.floor(r2 / 30), g + 22 + dy, p.z + dz, M('hull_white'));
    }
  b.box(p.x - 30, g, p.z - 1, p.x - 28, g + 22, p.z + 1, M('steel_dark_1'));
  loot.add(p.x + 43, p.z, g + 10);
  loot.add(p.x - 20, p.z + 20, g + 10);
  loot.add(p.x, p.z - 24, g + 10, 'armor');
}

function docks(b: MapBuilder, loot: Loot): void {
  const p = POIS.docks;
  const deck = planks([M('planks_4')], 0);
  const post = M('log_2');
  // Two piers from the beach out over the bay, on posts down to the sea floor.
  for (const [x0, z0, x1, z1] of [[960, 1030, 990, 1180], [1000, 1060, 1150, 1086]] as const) {
    b.box(x0, SEA, z0, x1, SEA + 2, z1, deck);
    for (let x = x0; x < x1; x += 18)
      for (let z = z0; z < z1; z += 18) b.box(x, SEA - 4, z, x + 3, SEA + 2, z + 3, post);
    b.clear(x0, SEA + 2, z0, x1, SEA + 30, z1);
  }
  // Boathouse on the shore, a moored boat and the fuel depot (explosive).
  b.building({
    x0: 880, z0: 950, x1: 950, z1: 1020, y0: p.y - 1, floors: 1, floorH: 34, wall: planks([M('planks_dark_2')], 0), roof: M('roof_slate'),
    trim: M('stone_4'), windows: { w: 8, h: 8, sill: 10, spacing: 18, glass: null }, doors: [{ side: 's', w: 20, h: 26 }, { side: 'e', w: 12 }],
  });
  const boat = (x: number, z: number, color: number): void => {
    b.box(x, SEA, z, x + 20, SEA + 6, z + 50, color);
    b.box(x + 2, SEA + 2, z + 2, x + 18, SEA + 6, z + 48, 0);
    b.box(x + 4, SEA, z - 6, x + 16, SEA + 6, z, color);
    b.box(x + 2, SEA + 1, z + 4, x + 18, SEA + 2, z + 46, M('planks_2'));
    b.box(x + 6, SEA + 6, z + 30, x + 14, SEA + 14, z + 40, M('paint_white'));
  };
  boat(1000, 1100, M('paint_red'));
  boat(1040, 1100, M('paint_teal'));
  for (const [x, z] of [[900, 1040], [912, 1046]]) b.tntBarrel(x, p.y, z);
  b.crate(930, p.y, 1050);
  b.crate(940, p.y, 1060, 8);
  loot.add(915, 985, p.y + 10);
  loot.add(975, 1150, SEA + 12);
  loot.add(1120, 1073, SEA + 12);
  loot.add(1030, 1125, SEA + 12, 'gadget');
}

function ruins(b: MapBuilder, loot: Loot): void {
  const p = POIS.ruins;
  const g = p.y;
  const st = blocks([M('mossy_stone'), M('stone_2')], 3, 7);
  const st2 = blocks([M('stone_1')], 3, 8);
  b.box(p.x - 60, g - 1, p.z - 70, p.x + 60, g, p.z + 70, blocks([M('cobble_1')], 3, 9));
  for (const [x0, z0, x1, z1, hh] of [
    [-60, -70, 20, -62, 30], [36, -70, 60, -62, 18], [-60, -70, -52, 10, 40], [-60, 30, -52, 70, 22], [52, -40, 60, 70, 34], [-20, 62, 60, 70, 16],
  ] as const)
    b.box(p.x + x0, g, p.z + z0, p.x + x1, g + hh, p.z + z1, st);
  for (const [dx, dz, hh] of [[-24, -30, 40], [24, -30, 24], [-24, 30, 12], [24, 30, 44]] as const) b.octagon(p.x + dx, p.z + dz, g, g + hh, 5, st2);
  // Hollow, roofless stump of a collapsed tower with a doorway.
  b.octagon(p.x - 30, p.z + 46, g, g + 50, 14, st);
  b.octagon(p.x - 30, p.z + 46, g, g + 50, 10, 0);
  b.clear(p.x - 30, g, p.z + 30, p.x - 24, g + 22, p.z + 38);
  loot.add(p.x, p.z, g + 10, 'weapon');
  loot.add(p.x + 30, p.z + 40, g + 10);
  loot.add(p.x - 30, p.z + 46, g + 10);
  loot.add(p.x + 40, p.z - 50, g + 10);
}

function camp(b: MapBuilder, loot: Loot): void {
  const p = POIS.camp;
  const g = p.y;
  b.tent(p.x - 60, g, p.z - 30, true, 36, 26, M('cloth_green'));
  b.tent(p.x + 20, g, p.z - 40, false, 36, 26, M('cloth_yellow'));
  b.tent(p.x - 20, g, p.z + 30, true, 36, 26, M('cloth_red'));
  b.cylinder(p.x, p.z, g, g + 2, 6, M('stone_4'));
  b.box(p.x - 2, g + 2, p.z - 2, p.x + 3, g + 5, p.z + 3, M('neon_orange'));
  for (const [x0, z0, x1, z1] of [[-20, -10, -8, -8], [8, 10, 20, 12]] as const) b.box(p.x + x0, g, p.z + z0, p.x + x1, g + 4, p.z + z1, M('log_1'));
  b.crate(p.x + 40, g, p.z + 20);
  b.crate(p.x + 50, g, p.z + 24, 8);
  loot.add(p.x - 42, p.z - 16, g + 10);
  loot.add(p.x + 32, p.z - 22, g + 10);
  loot.add(p.x, p.z + 44, g + 10);
}

function quarry(b: MapBuilder, loot: Loot): void {
  const p = POIS.quarry;
  const g = p.y;
  // Stepped cut into the rock with blocks, a small crane mast and blasting charges.
  // Each level is cut inside the previous one, one 0.6 m step lower (walkable with a hop).
  for (let k = 0; k < 4; k++) {
    const r = 50 - k * 10;
    const fy = g - (k + 1) * 5;
    b.clear(p.x - r, fy, p.z - r, p.x + r, g + 4, p.z + r);
    b.box(p.x - r, fy - 1, p.z - r, p.x + r, fy, p.z + r, M('stone_3'));
  }
  for (const [dx, dz] of [[-24, 12], [8, -26], [16, 18]]) b.box(p.x + dx, g - 20, p.z + dz, p.x + dx + 10, g - 12, p.z + dz + 10, blocks([M('stone_1')], 3, 3));
  b.lattice(p.x + 60, g, p.z - 70, p.x + 72, g + 70, p.z - 58, 'y', M('scaffold'), 2, 12);
  b.lattice(p.x + 20, g + 62, p.z - 68, p.x + 72, g + 70, p.z - 60, 'x', M('scaffold'), 2, 10);
  b.tntCrate(p.x - 8, g - 20, p.z - 8, 7);
  b.tntCrate(p.x - 64, g, p.z + 34, 7);
  loot.add(p.x, p.z + 20, g, 'gadget');
  loot.add(p.x + 70, p.z, g + 10);
  loot.add(p.x - 60, p.z + 60, g + 10);
}

/** Round-canopied tree built from octagon slabs (cheap to mesh). */
function roundTree(b: MapBuilder, x: number, y: number, z: number, h: number, r: number, leaves: number): void {
  b.box(x - 1, y, z - 1, x + 2, y + h, z + 2, M('log_1'));
  b.octagon(x, z, y + h - 6, y + h, Math.round(r * 0.8), leaves);
  b.octagon(x, z, y + h, y + h + 8, r, leaves);
  b.octagon(x, z, y + h + 8, y + h + 13, Math.round(r * 0.65), leaves);
}

function build(b: MapBuilder): MapMeta {
  const { h, surf } = terrain(b);
  const loot = new Loot(b);
  town(b, loot);
  farm(b, loot);
  lighthouse(b, loot);
  radio(b, loot);
  docks(b, loot);
  ruins(b, loot);
  camp(b, loot);
  quarry(b, loot);
  const meta = emptyMeta(SEA / 10, S / 20, S / 20);

  // Spawns ring the island: walk outward from the middle to the beach, then step back inland.
  const spawnVox: [number, number][] = [];
  for (let k = 0; k < 24; k++) {
    const a = (k / 24) * Math.PI * 2 + 0.13;
    const dx = dcos(a);
    const dz = dsin(a);
    let x = 640;
    let z = 640;
    for (let r = 200; r < 640; r += 4) {
      const tx = Math.round(640 + dx * r);
      const tz = Math.round(640 + dz * r);
      if (tx < 20 || tz < 20 || tx >= S - 20 || tz >= S - 20) break;
      if (h[tx + tz * S] <= SEA + 3) break;
      x = tx;
      z = tz;
    }
    spawnVox.push([Math.round(x - dx * 30), Math.round(z - dz * 30)]);
  }

  // Scatter trees on grass away from POIs, paths and spawns; pines up high, palms on the sand.
  const grassSet = new Set(family('grass'));
  const pois = Object.values(POIS);
  const sandDry = M('sand_3');
  const sandWet = M('sand_1');
  const clearOf = (x: number, z: number, pad: number): boolean =>
    !pois.some((p) => Math.hypot(x - p.x, z - p.z) < p.r + pad) &&
    !PATHS.some(([ax, az, bx, bz]) => segDist(x, z, ax, az, bx, bz) < 22) &&
    !spawnVox.some(([sx, sz]) => Math.hypot(x - sx, z - sz) < 60);
  // Woods: dense where the forest noise is high, a few lone trees elsewhere.
  const forest = (x: number, z: number): number => fbm2(x * 0.004, z * 0.004, 504, 2);
  for (let gz = 40; gz < S - 40; gz += 76)
    for (let gx = 40; gx < S - 40; gx += 76) {
      const hh = hash32(gx, gz, 77);
      const x = gx + (hh % 36);
      const z = gz + ((hh >>> 8) % 36);
      const i = x + z * S;
      const y = h[i];
      const f = forest(x, z);
      if (y <= SEA + 1 || (f < 0.55 && (hh >>> 16) % 5 !== 0) || !clearOf(x, z, 20)) continue;
      const s = surf[i];
      if (grassSet.has(s)) {
        // Tree tops stay below y = 128 (the renderer merges 64-voxel-tall regions: a canopy poking
        // into the next region layer costs an extra draw call). High ground gets shrubs instead.
        const pineH = Math.min(50 + ((hh >>> 20) % 16), TREE_CAP - 6 - y);
        const r = 9 + ((hh >>> 24) % 3);
        const roundH = Math.min(30 + ((hh >>> 20) % 12), TREE_CAP - 13 - y);
        if (y > SEA + 34 && pineH >= 34) b.blockyPine(x, y, z, pineH);
        else if (roundH >= 18) roundTree(b, x, y, z, roundH, r, (hh >>> 26) % 2 ? M('leaves_2') : M('leaves_1'));
        else b.bush(x, y, z, 7, M('leaves_3'));
      } else if (s === sandDry || s === sandWet) {
        const dirs: Side[] = ['n', 's', 'e', 'w'];
        b.palm(x, y, z, Math.min(40 + ((hh >>> 20) % 14), TREE_CAP - 4 - y), dirs[(hh >>> 24) % 4]);
      }
    }
  // Palms along the beaches.
  for (let k = 0; k < 40; k++) {
    const a = (k / 40) * Math.PI * 2 + 0.05;
    const hh = hash32(k, 17, 3);
    if (hh % 3 === 0) continue;
    let x = 640;
    let z = 640;
    for (let r = 300; r < 640; r += 3) {
      const tx = Math.round(640 + dcos(a) * r);
      const tz = Math.round(640 + dsin(a) * r);
      if (tx < 8 || tz < 8 || tx >= S - 8 || tz >= S - 8 || h[tx + tz * S] < SEA) break;
      x = tx;
      z = tz;
    }
    x = Math.round(x - dcos(a) * (8 + (hh % 14)));
    z = Math.round(z - dsin(a) * (8 + (hh % 14)));
    const y = h[x + z * S];
    if (y <= SEA || y > SEA + 6 || !clearOf(x, z, 10)) continue;
    const dirs: Side[] = ['n', 's', 'e', 'w'];
    b.palm(x, y, z, Math.min(38 + ((hh >>> 8) % 14), TREE_CAP - 4 - y), dirs[(hh >>> 12) % 4]);
  }
  // Boulders on the hills.
  for (let k = 0; k < 14; k++) {
    const hh = hash32(k, 91, 5);
    const x = 120 + (hh % 1040);
    const z = 120 + ((hh >>> 11) % 1040);
    const y = h[x + z * S];
    if (y <= SEA + 6 || !clearOf(x, z, 20)) continue;
    b.rock(x, y - 2, z, 6 + (hh % 5), M('stone_2'));
  }

  for (const [x, z] of spawnVox) {
    const xm = (x + 0.5) / 10;
    const zm = (z + 0.5) / 10;
    meta.spawns.push({ team: -1, x: xm, y: b.standAt(xm, zm, HEIGHT / 10 - 1), z: zm, yaw: yawToward(xm, zm, 64, 64) });
  }
  // Extra loot in the open along the paths.
  for (const [ax, az, bx, bz] of PATHS) {
    const len = Math.hypot(bx - ax, bz - az);
    for (const t of [0.55, 0.8]) {
      const x = Math.round(ax + (bx - ax) * t - ((bz - az) / len) * 8);
      const z = Math.round(az + (bz - az) * t + ((bx - ax) / len) * 8);
      loot.add(x, z, h[x + z * S] + 1);
    }
  }
  meta.pickups.push(...loot.spots);
  return meta;
}

export const voxelIsland: MapDef = {
  id: 'voxel_island',
  name: 'Voxel Island',
  description: 'Drop onto a sunny island: a town, a farm, a lighthouse, a radio hill, docks, ruins and a quarry. Last marine standing wins.',
  size: [S, HEIGHT, S],
  modes: ['br', 'ffa', 'sandbox', 'practice'],
  players: [4, 20],
  seed: 4242,
  version: 2,
  theme: {
    skyTop: 0x3a8ae6,
    skyHorizon: 0xd6ecfa,
    fog: 0xcfe4f4,
    fogNear: 70,
    fogFar: 230,
    sun: [0.4, 0.82, 0.3],
    sunColor: 0xfff4e0,
    sunIntensity: 1.0,
    ambient: 0xbcd4f4,
    ambientIntensity: 0.56,
    outside: 0x4d92e6,
    music: 'island',
  },
  build,
};
